import type { Product, Venue, VenueReservation } from "@/graphql/operations";
import type { LazyQueryExecFunction } from "@apollo/client";
import { startOfMonth } from "date-fns";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { useCallback } from "react";
import type {
  GetVenueReservationsRangeQueryInput,
  GetVenueReservationsRangeQueryResponse,
} from "./queries/GetVenueReservationsQuery";
import { getEdgeReservationDate } from "./reservationTime";
import { getBucketKey } from "./reservationsBuckets";
import {
  fetchReservationsForSearch,
  type DefaultVariables,
} from "./reservationsFetchHelpers";
import {
  DEFAULT_FUTURE_MONTHS,
  DEFAULT_PAST_MONTHS,
  type FetchReservationsOptions,
  type FetchedMonthsRange,
  type ReservationSearch,
  type ReservationsBucket,
} from "./reservationsTypes";

type UseReservationFetchOptions = {
  userId?: string;
  from: Date | null;
  until: Date | null;
  reservations: VenueReservation[];
  fetchedMonthsRange: FetchedMonthsRange;
  appliedSearchVariables: ReservationSearch;
  defaultVariables: DefaultVariables;
  canFetchMorePastReservations: boolean;
  canFetchMoreUpcomingReservations: boolean;
  fetchReservationsQuery: LazyQueryExecFunction<
    GetVenueReservationsRangeQueryResponse,
    GetVenueReservationsRangeQueryInput
  >;
  getOrCreateBucket: (key: string) => ReservationsBucket;
  updateBucket: (
    key: string,
    updater: (bucket: ReservationsBucket) => ReservationsBucket,
  ) => ReservationsBucket;
  resetBucket: (
    key: string,
    options?: { clearApollo?: boolean },
  ) => ReservationsBucket;
  activeBucketKeyRef: MutableRefObject<string>;
  setAppliedVenueId: Dispatch<SetStateAction<Venue["id"] | null>>;
  setAppliedProductId: Dispatch<SetStateAction<Product["id"] | null>>;
};

/**
 * Handles fetching reservations and paging through month ranges.
 */
export default function useReservationFetch({
  userId,
  from,
  until,
  reservations,
  fetchedMonthsRange,
  appliedSearchVariables,
  defaultVariables,
  canFetchMorePastReservations,
  canFetchMoreUpcomingReservations,
  fetchReservationsQuery,
  getOrCreateBucket,
  updateBucket,
  resetBucket,
  activeBucketKeyRef,
  setAppliedVenueId,
  setAppliedProductId,
}: UseReservationFetchOptions) {
  /**
   * Fetches reservations for an explicit search payload.
   */
  const fetchReservationsForSearchCallback = useCallback(
    async (search: ReservationSearch, options?: FetchReservationsOptions) =>
      fetchReservationsForSearch({
        userId,
        from,
        until,
        search,
        options,
        defaultVariables,
        fetchReservationsQuery,
        getOrCreateBucket,
        updateBucket,
        pastDefault: DEFAULT_PAST_MONTHS,
        futureDefault: DEFAULT_FUTURE_MONTHS,
      }),
    [
      userId,
      from,
      until,
      defaultVariables,
      fetchReservationsQuery,
      getOrCreateBucket,
      updateBucket,
    ],
  );

  /**
   * Fetches reservations using the currently applied search parameters.
   */
  const fetchReservations = useCallback(
    async (options?: FetchReservationsOptions) =>
      fetchReservationsForSearchCallback(appliedSearchVariables, options),
    [fetchReservationsForSearchCallback, appliedSearchVariables],
  );

  /**
   * Loads one more past month range when available.
   */
  const fetchMorePastReservations = useCallback(async () => {
    if (!canFetchMorePastReservations) return;
    const baseMonth =
      fetchedMonthsRange.from ??
      getEdgeReservationDate(reservations, "start") ??
      new Date();
    await fetchReservations({
      month: startOfMonth(baseMonth),
      past: 1,
    });
  }, [
    canFetchMorePastReservations,
    fetchReservations,
    fetchedMonthsRange.from,
    reservations,
  ]);

  /**
   * Loads one more upcoming month range when available.
   */
  const fetchMoreUpcomingReservations = useCallback(async () => {
    if (!canFetchMoreUpcomingReservations) return;
    const baseMonth =
      fetchedMonthsRange.until ??
      getEdgeReservationDate(reservations, "end") ??
      new Date();
    await fetchReservations({
      month: startOfMonth(baseMonth),
      future: 1,
    });
  }, [
    canFetchMoreUpcomingReservations,
    fetchReservations,
    fetchedMonthsRange.until,
    reservations,
  ]);

  /**
   * Resets the bucket and fetches the initial month range for a search.
   */
  const fetchInitialReservations = useCallback(
    async (overrideSearch?: ReservationSearch, baseDateOverride?: Date) => {
      const search = overrideSearch ?? appliedSearchVariables;
      const key = getBucketKey(search);
      activeBucketKeyRef.current = key;
      if (overrideSearch) {
        setAppliedVenueId(search.venueId ?? null);
        setAppliedProductId(search.productId ?? null);
      }
      resetBucket(key, { clearApollo: true });
      const baseDate = baseDateOverride ?? from ?? until ?? new Date();
      await fetchReservationsForSearchCallback(search, {
        month: baseDate,
        past: DEFAULT_PAST_MONTHS,
        future: DEFAULT_FUTURE_MONTHS,
      });
    },
    [
      appliedSearchVariables,
      fetchReservationsForSearchCallback,
      from,
      resetBucket,
      until,
      setAppliedVenueId,
      setAppliedProductId,
      activeBucketKeyRef,
    ],
  );

  return {
    fetchReservationsForSearch: fetchReservationsForSearchCallback,
    fetchReservations,
    fetchMorePastReservations,
    fetchMoreUpcomingReservations,
    fetchInitialReservations,
  };
}
